import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { mdiChevronDown, mdiChevronUp, mdiCodeJson, mdiLightningBolt, mdiMinus, mdiPencil, mdiPlus } from '@mdi/js';
import Icon from '@mdi/react';
import { CodingStatusType } from '../../../../api';
import { renderDate, Variables } from '../../../..';
import { TimelineStatusTag } from '../timeline-status-tag';
import * as Cd from './coding-detail.styles';
import { Coding, ICodingDiffCodeLine, ICodingDiffDisplayLine, ICodingDiffRegion, ICodingDiffResult, ICodingFieldChange } from './coding-detail.types';
import {
    buildCodingDiffLines,
    codingHeadlineTitle,
    codingStatusToTone,
    computeCodingFieldChanges,
    formatCodingChangesSummary,
    getCodingPayload,
    parseCodingNotes,
} from './coding-detail.utils';

const EMPTY_DIFF: ICodingDiffResult = { segments: [], regions: [], allLines: [] };

const CodingJsonLine: React.FC<{ text: string }> = ({ text }) => {
    const match = text.match(/^(\s*)"([^"]+)":\s*(.*)$/);
    if (!match) {
        return <Cd.CdJsonPlain>{text}</Cd.CdJsonPlain>;
    }

    const [, indent, key, value] = match;
    const isString = /^"/.test(value);

    return (
        <>
            {indent}
            <Cd.CdJsonKey>&quot;{key}&quot;</Cd.CdJsonKey>
            <Cd.CdJsonColon>: </Cd.CdJsonColon>
            {isString ? <Cd.CdJsonString>{value}</Cd.CdJsonString> : <Cd.CdJsonPrimitive>{value}</Cd.CdJsonPrimitive>}
        </>
    );
};

const changeOpLabel = (op: ICodingFieldChange['op']): string => {
    switch (op) {
        case 'added':
            return 'Added';
        case 'changed':
            return 'Changed';
        case 'removed':
            return 'Removed';
        default:
            return op;
    }
};

const changeOpIcon = (op: ICodingFieldChange['op']): string => {
    switch (op) {
        case 'added':
            return mdiPlus;
        case 'changed':
            return mdiPencil;
        case 'removed':
            return mdiMinus;
        default:
            return mdiPlus;
    }
};

const renderDiffSegment = (segment: ICodingDiffDisplayLine, key: string) => {
    if (segment.kind === 'ellipsis') {
        return (
            <Cd.CdDiffEllipsis key={key}>
                <Cd.CdDiffEllipsisDot />
                {segment.label}
            </Cd.CdDiffEllipsis>
        );
    }

    return (
        <Cd.CdDiffLine key={key} $added={segment.added}>
            <Cd.CdDiffMarker $added={segment.added}>{segment.added ? '+' : ''}</Cd.CdDiffMarker>
            <Cd.CdDiffLineNum>{segment.n}</Cd.CdDiffLineNum>
            <Cd.CdDiffCode $added={segment.added}>
                <CodingJsonLine text={segment.text} />
            </Cd.CdDiffCode>
        </Cd.CdDiffLine>
    );
};

interface ICodingEmbeddedPanelProps {
    coding: Coding;
    embedded?: boolean;
}

export const CodingEmbeddedPanel: React.FunctionComponent<ICodingEmbeddedPanelProps> = ({
    coding,
    embedded = true,
}) => {
    const [showRaw, setShowRaw] = useState(false);
    const [showFullPayload, setShowFullPayload] = useState(false);
    const [activeRegionIndex, setActiveRegionIndex] = useState(0);
    const regionRefs = useRef<Record<number, HTMLDivElement | null>>({});
    const diffScrollRef = useRef<HTMLDivElement | null>(null);

    const isSubmitted = coding.status === CodingStatusType.SUBMITTED;
    const shouldAnalyzeDiff = !isSubmitted;

    const rules = useMemo(() => parseCodingNotes(coding.notes), [coding.notes]);
    const beforePayload = useMemo(() => getCodingPayload(coding, 'before'), [coding]);
    const afterPayload = useMemo(() => getCodingPayload(coding, 'after'), [coding]);
    const fieldChanges = useMemo(
        () => (shouldAnalyzeDiff ? computeCodingFieldChanges(beforePayload, afterPayload) : []),
        [shouldAnalyzeDiff, beforePayload, afterPayload],
    );
    const changesSummary = useMemo(
        () => (isSubmitted ? 'awaiting coding results' : formatCodingChangesSummary(fieldChanges)),
        [isSubmitted, fieldChanges],
    );
    const diffData = useMemo(
        () => (shouldAnalyzeDiff ? buildCodingDiffLines(beforePayload, afterPayload) : EMPTY_DIFF),
        [shouldAnalyzeDiff, beforePayload, afterPayload],
    );

    const regionCount = diffData.regions.length;
    const hasChangeRegions = regionCount > 0;
    const truncatedPreviewLines = useMemo(
        () => diffData.segments.filter((segment): segment is ICodingDiffCodeLine => segment.kind === 'code'),
        [diffData.segments],
    );
    // With change regions: always render the full payload (scrolled to changes).
    // With no added lines: honor buildCodingDiffLines truncation (first 20) unless expanded.
    const displayLines =
        hasChangeRegions || showFullPayload ? diffData.allLines : truncatedPreviewLines;
    const canToggleFullPayload =
        !hasChangeRegions && diffData.allLines.length > truncatedPreviewLines.length;
    const hasMultipleRegions = regionCount > 1;
    const hasRawPayloadContent = diffData.allLines.length > 0;
    const regionsByLineIndex = useMemo(() => {
        const byIndex = new Map<number, ICodingDiffRegion>();
        for (const region of diffData.regions) {
            byIndex.set(region.allLineIndex, region);
        }
        return byIndex;
    }, [diffData.regions]);

    const rawPayloadEmptyMessage = useMemo(() => {
        if (afterPayload == null) {
            return 'No after payload was recorded for this coding pass.';
        }

        return 'No line-level differences to display for this payload.';
    }, [afterPayload]);

    const rawPayloadFallbackBody = useMemo(() => {
        if (afterPayload == null) {
            return '{}';
        }

        try {
            return JSON.stringify(afterPayload, null, 2) || '{}';
        } catch {
            return '{}';
        }
    }, [afterPayload]);

    const scrollToRegion = useCallback(
        (regionIndex: number, behavior: ScrollBehavior = 'smooth') => {
            const region = diffData.regions[regionIndex];
            if (!region) {
                return;
            }

            const anchor = regionRefs.current[region.id];
            const container = diffScrollRef.current;
            if (anchor && container) {
                const nextTop =
                    container.scrollTop +
                    (anchor.getBoundingClientRect().top - container.getBoundingClientRect().top) -
                    8;
                container.scrollTo({ top: Math.max(0, nextTop), behavior });
            }
            setActiveRegionIndex(regionIndex);
        },
        [diffData.regions],
    );

    const goToPreviousRegion = useCallback(() => {
        if (!hasMultipleRegions) {
            return;
        }
        scrollToRegion((activeRegionIndex - 1 + regionCount) % regionCount);
    }, [activeRegionIndex, hasMultipleRegions, regionCount, scrollToRegion]);

    const goToNextRegion = useCallback(() => {
        if (!hasMultipleRegions) {
            return;
        }
        scrollToRegion((activeRegionIndex + 1) % regionCount);
    }, [activeRegionIndex, hasMultipleRegions, regionCount, scrollToRegion]);

    useEffect(() => {
        if (!showRaw) {
            setShowFullPayload(false);
            setActiveRegionIndex(0);
            return;
        }

        if (regionCount === 0) {
            return;
        }

        // Jump viewport to the first change region once the full payload is mounted.
        const frameId = requestAnimationFrame(() => {
            scrollToRegion(0, 'auto');
        });
        return () => cancelAnimationFrame(frameId);
    }, [showRaw, regionCount, scrollToRegion]);

    const statusLabel = coding.status || 'Unknown';
    const headlineTitle = codingHeadlineTitle(coding.status);

    return (
        <Cd.CdPanel $embedded={embedded}>
            <Cd.CdHeadlineCard $embedded={embedded}>
                <Cd.CdIconWrap>
                    <Icon path={mdiCodeJson} size={0.9} color={Variables.ThemeLight.PrimaryConstant6} />
                </Cd.CdIconWrap>
                <Cd.CdHeadlineMain>
                    <Cd.CdHeadlineTitleRow>
                        <Cd.CdHeadlineTitle>{headlineTitle}</Cd.CdHeadlineTitle>
                        <TimelineStatusTag tone={codingStatusToTone(coding.status)} label={statusLabel} />
                    </Cd.CdHeadlineTitleRow>
                    <Cd.CdHeadlineMeta>
                        Automated pass by <strong>{coding.vendor || 'Unknown vendor'}</strong>
                        {coding.created ? <> · {renderDate(coding.created)}</> : null}
                    </Cd.CdHeadlineMeta>
                    <Cd.CdHeadlineSummary>
                        <Icon path={mdiLightningBolt} size={0.67} color={Variables.ThemeLight.AttentionConstant6} />
                        <span>
                            <strong>{rules.length} rules</strong> applied · <strong>{changesSummary}</strong>
                        </span>
                    </Cd.CdHeadlineSummary>
                </Cd.CdHeadlineMain>
                <Cd.CdHeadlineAside>
                    <Cd.CdLabel>Coding ID</Cd.CdLabel>
                    <Cd.CdMonoValue>{coding.id}</Cd.CdMonoValue>
                </Cd.CdHeadlineAside>
            </Cd.CdHeadlineCard>

            <Cd.CdSectionCard $embedded={embedded}>
                <Cd.CdSectionTitle>Rules Applied</Cd.CdSectionTitle>
                {rules.length > 0 ? (
                    <Cd.CdRulesList>
                        {rules.map((rule) => (
                            <Cd.CdRuleCard key={`${rule.n}-${rule.text}`}>
                                <Cd.CdRuleBadge>Rule {rule.n}</Cd.CdRuleBadge>
                                <Cd.CdRuleText>{rule.text}</Cd.CdRuleText>
                            </Cd.CdRuleCard>
                        ))}
                    </Cd.CdRulesList>
                ) : (
                    <Cd.CdEmptyState>No rules recorded for this coding pass.</Cd.CdEmptyState>
                )}
            </Cd.CdSectionCard>

            <Cd.CdSectionCard $embedded={embedded}>
                <Cd.CdChangesHeader>
                    <Cd.CdSectionTitle style={{ margin: 0 }}>Changes to Claim</Cd.CdSectionTitle>
                    {shouldAnalyzeDiff && (
                        <Cd.CdToggleButton
                            type="button"
                            variant="unstyled"
                            $active={showRaw}
                            onClick={() => setShowRaw((value) => !value)}>
                            <Icon path={mdiCodeJson} size={0.62} />
                            {showRaw ? 'Hide raw payload' : 'View raw payload'}
                        </Cd.CdToggleButton>
                    )}
                </Cd.CdChangesHeader>

                {isSubmitted ? (
                    <Cd.CdEmptyState>
                        Coding is still submitted. Diff analysis runs after coding completes.
                    </Cd.CdEmptyState>
                ) : fieldChanges.length > 0 ? (
                    <Cd.CdChangesList $scrollable={fieldChanges.length > 5}>
                        {fieldChanges.map((change) => (
                            <Cd.CdChangeCard key={change.field} $op={change.op}>
                                <Cd.CdChangeOp $op={change.op}>
                                    <Icon path={changeOpIcon(change.op)} size={0.58} />
                                    {changeOpLabel(change.op)}
                                </Cd.CdChangeOp>
                                <Cd.CdChangeBody>
                                    <Cd.CdChangeLabel>{change.label}</Cd.CdChangeLabel>
                                    <Cd.CdChangeField>{change.field}</Cd.CdChangeField>
                                </Cd.CdChangeBody>
                                <Cd.CdChangeValue>{change.value}</Cd.CdChangeValue>
                            </Cd.CdChangeCard>
                        ))}
                    </Cd.CdChangesList>
                ) : (
                    <Cd.CdEmptyState>No field-level changes detected between before and after payloads.</Cd.CdEmptyState>
                )}

                {shouldAnalyzeDiff && showRaw && (
                    <Cd.CdDiffWrap>
                        <Cd.CdDiffHeader>
                            <Cd.CdDiffTitle>Payload diff</Cd.CdDiffTitle>
                            <Cd.CdDiffSubtitle>
                                {hasRawPayloadContent
                                    ? hasChangeRegions
                                      ? regionCount > 1
                                        ? `full after payload · ${regionCount} change regions`
                                        : 'full after payload · scrolled to change'
                                      : showFullPayload
                                        ? 'full after payload · line numbers'
                                        : `preview · first ${displayLines.length} lines`
                                    : 'no diff available'}
                            </Cd.CdDiffSubtitle>
                            {hasRawPayloadContent && hasMultipleRegions && (
                                <Cd.CdDiffNav>
                                    <Cd.CdDiffNavButton type="button" variant="unstyled" onClick={goToPreviousRegion}>
                                        <Icon path={mdiChevronUp} size={0.62} />
                                        Prev
                                    </Cd.CdDiffNavButton>
                                    <Cd.CdDiffNavCount>
                                        Change {activeRegionIndex + 1} of {regionCount}
                                    </Cd.CdDiffNavCount>
                                    <Cd.CdDiffNavButton type="button" variant="unstyled" onClick={goToNextRegion}>
                                        Next
                                        <Icon path={mdiChevronDown} size={0.62} />
                                    </Cd.CdDiffNavButton>
                                </Cd.CdDiffNav>
                            )}
                            {hasRawPayloadContent && (
                                <Cd.CdDiffNav>
                                    {canToggleFullPayload && (
                                        <Cd.CdDiffNavButton
                                            type="button"
                                            variant="unstyled"
                                            onClick={() => setShowFullPayload((value) => !value)}>
                                            {showFullPayload ? 'Show fewer lines' : 'Show all lines'}
                                        </Cd.CdDiffNavButton>
                                    )}
                                    <Cd.CdDiffLegend>
                                        <Cd.CdDiffLegendSwatch />
                                        Added
                                    </Cd.CdDiffLegend>
                                </Cd.CdDiffNav>
                            )}
                        </Cd.CdDiffHeader>
                        {hasRawPayloadContent ? (
                        <Cd.CdDiffScroll ref={diffScrollRef}>
                            <Cd.CdDiffBody>
                                {displayLines.map((line, index) => {
                                    const region = regionsByLineIndex.get(index);
                                    if (region) {
                                        return (
                                            <Cd.CdDiffRegionAnchor
                                                key={`${line.n}-${line.text}-${index}`}
                                                ref={(node) => {
                                                    regionRefs.current[region.id] = node;
                                                }}>
                                                {renderDiffSegment(line, `${line.n}-${line.text}-${index}`)}
                                            </Cd.CdDiffRegionAnchor>
                                        );
                                    }

                                    return renderDiffSegment(line, `${line.n}-${line.text}-${index}`);
                                })}
                            </Cd.CdDiffBody>
                        </Cd.CdDiffScroll>
                        ) : (
                            <Cd.CdDiffEmptyPayload>
                                <Cd.CdDiffEmptyPayloadText>{rawPayloadEmptyMessage}</Cd.CdDiffEmptyPayloadText>
                                <Cd.CdDiffEmptyPayloadCode>{rawPayloadFallbackBody}</Cd.CdDiffEmptyPayloadCode>
                            </Cd.CdDiffEmptyPayload>
                        )}
                    </Cd.CdDiffWrap>
                )}
            </Cd.CdSectionCard>
        </Cd.CdPanel>
    );
};
